/** Clinician's working copy of an LMN draft, backed by the CopilotShelf.
 *
 *  The tool output carries the LLM's markdown. As the clinician edits,
 *  we debounce the working copy into a shelf entry keyed by
 *  ``lmn_edit:{toolCallId}`` so it survives re-render and navigation. A
 *  re-draft is a new tool call, hence a new key and a clean slate; the
 *  entry is patient/clinician-scoped because the shelf row is.
 */

'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { shelfLoad, shelfStore } from '@/shared/api/lotus/shelf';

const AUTOSAVE_DEBOUNCE_MS = 1500;

function shelfKey(toolCallId: string): string {
  return `lmn_edit:${toolCallId}`;
}

function readSavedMarkdown(value: Array<Record<string, unknown>>): string | null {
  const markdown = value[0]?.markdown;
  return typeof markdown === 'string' ? markdown : null;
}

export interface UseLmnDraftEditResult {
  value: string;
  onChange: (next: string) => void;
  /** Flush any pending debounced write and return the latest markdown so
   *  a click handler reads what the editor has, not a stale snapshot. */
  getLatest: () => string;
}

export function useLmnDraftEdit(params: {
  patientId: string;
  toolCallId: string;
  initialMarkdown: string;
}): UseLmnDraftEditResult {
  const { patientId, toolCallId, initialMarkdown } = params;

  const [value, setValue] = useState(initialMarkdown);
  const valueRef = useRef(initialMarkdown);
  const pendingRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const editedRef = useRef(false);

  const store = useCallback(
    (markdown: string, options?: { keepalive?: boolean }) =>
      shelfStore(patientId, shelfKey(toolCallId), [{ markdown }], {}, options),
    [patientId, toolCallId]
  );

  const flush = useCallback(
    (options?: { keepalive?: boolean }) => {
      const pending = pendingRef.current;
      pendingRef.current = null;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (pending !== null) void store(pending, options);
    },
    [store]
  );

  // Load the saved edit on mount; fall back to the LLM original on a
  // miss. Don't clobber edits the clinician made while the load was
  // still in flight.
  useEffect(() => {
    let cancelled = false;
    void shelfLoad(patientId, shelfKey(toolCallId))
      .then((result) => {
        if (cancelled || editedRef.current) return;
        const saved = readSavedMarkdown(result.value);
        if (saved !== null) {
          setValue(saved);
          valueRef.current = saved;
        }
      })
      .catch(() => {
        // No saved edit yet; the LLM original stands until the next change.
      });
    return () => {
      cancelled = true;
    };
  }, [patientId, toolCallId]);

  const onChange = useCallback(
    (next: string) => {
      editedRef.current = true;
      setValue(next);
      valueRef.current = next;
      pendingRef.current = next;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => flush(), AUTOSAVE_DEBOUNCE_MS);
    },
    [flush]
  );

  // Flush on unmount and tab close so a quick "type then leave" keeps the
  // edit. The unload path needs keepalive — a plain fetch is aborted once
  // the document tears down.
  useEffect(() => {
    const onUnload = () => flush({ keepalive: true });
    window.addEventListener('beforeunload', onUnload);
    return () => {
      window.removeEventListener('beforeunload', onUnload);
      flush();
    };
  }, [flush]);

  const getLatest = useCallback(() => {
    flush();
    return valueRef.current;
  }, [flush]);

  return { value, onChange, getLatest };
}
